import { useCallback, useEffect, useRef, useState } from "preact/hooks"

// useFetch is the one shape for a section of a page that comes from a GET.
// It keeps the three states every fetched thing needs — loading (data null,
// no error), failed (error set), loaded (data non-null, possibly empty) — and
// nothing else: the call site still decides what each looks like (a spinner,
// LoadError with Retry, the empty-state copy), and it may only render the
// empty copy from `data`, never from a null. See LoadError.tsx for why.
//
// Two ways to run the fetch again, because they look different on screen:
//
// - reload(): start over — drop the data, clear the error, fetch. This is
//   what runs on mount, and what a Retry button calls.
// - refresh(): fetch in the background and keep what is on screen. A success
//   replaces the data and clears the error; a failure sets the error and
//   leaves the stale data up. For polls and for re-reading after a mutation,
//   where blanking the list to a spinner would only make it flicker.
//
// setData edits the loaded value in place (drop the row the user just
// removed) when a round trip would only make it flicker. The fetcher is read
// fresh on every run, so an inline closure over props never goes stale.
//
// A response from a run that has been superseded (reload() while one is in
// flight) is ignored, so a slow old answer never lands on top of a newer one.
export function useFetch<T>(fetcher: () => Promise<T>, fallback: string) {
	const [data, setData] = useState<T | null>(null)
	const [error, setError] = useState("")
	const latest = useRef(fetcher)
	latest.current = fetcher
	const seq = useRef(0)

	const run = useCallback(
		(reset: boolean) => {
			const n = ++seq.current
			if (reset) {
				setData(null)
				setError("")
			}
			latest.current().then(
				(d) => {
					if (n !== seq.current) return
					setData(d)
					setError("")
				},
				(e: unknown) => {
					if (n !== seq.current) return
					setError(e instanceof Error ? e.message : fallback)
				},
			)
		},
		[fallback],
	)
	const reload = useCallback(() => run(true), [run])
	const refresh = useCallback(() => run(false), [run])
	useEffect(reload, [reload])

	return { data, error, reload, refresh, setData }
}
